import { jsPDF } from "jspdf";

export interface FinancialOperationMetricInput {
  id: string;
  aspspName?: string;
  accountName?: string;
  accountIban?: string;
  bookingDate?: string;
  valueDate?: string;
  direction: "Credit" | "Debit";
  amount: number;
  currency: string;
  counterpartyName?: string;
  counterpartyIban?: string;
  title?: string;
  status: "Booked" | "Pending";
  balanceAfter?: number;
  bankTransactionCode?: string;
  reference?: string;
}

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const fmtAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("pl-PL", { style: "currency", currency: currency || "PLN" }).format(amount);

const directionLabel = (d: "Credit" | "Debit") =>
  d === "Credit" ? "Wpływ (uznanie)" : "Wydatek (obciążenie)";

const statusLabel = (s: "Booked" | "Pending") =>
  s === "Pending" ? "Oczekująca" : "Zaksięgowana";

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateFinancialOperationMetricPdf(
  data: FinancialOperationMetricInput,
): Promise<Blob> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14;
  const R = 196;
  const W = 182;
  let y = 16;

  const setF = (size: number, bold = false, r = 28, g = 27, b = 31) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(r, g, b);
  };

  const hline = () => {
    doc.setLineWidth(0.35);
    doc.setDrawColor(93, 73, 199);
    doc.line(L, y, R, y);
  };

  const drawRow = (label: string, value: string) => {
    setF(7.5, true, 120, 110, 140);
    doc.text(label, L, y);
    setF(9, false, 28, 27, 31);
    const lines = doc.splitTextToSize(value || "—", W - 52);
    doc.text(lines, L + 52, y);
    y += lines.length * 5 + 1;
  };

  // ── Nagłówek ─────────────────────────────────────────────────────────
  setF(13, true, 93, 73, 199);
  doc.text("METRYKA OPERACJI FINANSOWEJ", L, y);
  y += 4;
  hline();
  y += 8;

  // ── Dane operacji ─────────────────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("DANE OPERACJI", L, y);
  y += 6;

  drawRow("TYTUŁ:", data.title ?? "—");
  drawRow("DATA KSIĘGOWANIA:", fmtDate(data.bookingDate));
  drawRow("DATA WALUTY:", fmtDate(data.valueDate));
  drawRow("KIERUNEK:", directionLabel(data.direction));
  const amtColor = data.direction === "Credit" ? [21, 127, 59] as const : [179, 38, 30] as const;
  setF(7.5, true, 120, 110, 140);
  doc.text("KWOTA:", L, y);
  setF(10, true, ...amtColor);
  doc.text(fmtAmount(data.amount, data.currency), L + 52, y);
  y += 6;
  drawRow("STATUS:", statusLabel(data.status));
  if (data.balanceAfter != null) {
    drawRow("SALDO PO OPERACJI:", fmtAmount(data.balanceAfter, data.currency));
  }
  if (data.reference) drawRow("REFERENCJA:", data.reference);
  if (data.bankTransactionCode) drawRow("KOD TRANSAKCJI:", data.bankTransactionCode);

  y += 4;
  hline();
  y += 8;

  // ── Kontrahent ────────────────────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("KONTRAHENT", L, y);
  y += 6;

  drawRow("NAZWA:", data.counterpartyName ?? "—");
  drawRow("NUMER RACHUNKU:", data.counterpartyIban ?? "—");

  y += 4;
  hline();
  y += 8;

  // ── Rachunek bankowy ──────────────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("RACHUNEK BANKOWY", L, y);
  y += 6;

  drawRow("BANK:", data.aspspName ?? "—");
  drawRow("RACHUNEK:", data.accountName || data.accountIban || "—");
  if (data.accountName && data.accountIban) {
    drawRow("IBAN:", data.accountIban);
  }

  y += 6;
  hline();
  y += 8;

  // ── Stopka ────────────────────────────────────────────────────────────
  setF(7.5, false, 130, 120, 140);
  doc.text(`Data wydruku: ${new Date().toLocaleDateString("pl-PL")}`, L, y);
  doc.text(".......................................", R, y, { align: "right" });
  y += 4.5;
  setF(7, false, 170, 165, 180);
  doc.text("podpis i pieczęć", R, y, { align: "right" });

  return doc.output("blob");
}
